import { useState, useRef } from 'react';
import { Box, Paper, Typography, IconButton, TextField, CircularProgress } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useIntl } from 'react-intl';
import AppPaperTitle from 'src/app/common/components/AppPaperTitle';
import AppPaperContent from 'src/app/common/components/AppPaperContent';
import { useBatchFilesSubmitPageContext } from '../../BatchFilesSubmitPage.context';
import useStyles from './AdditionalInformation.styles';
import { createBlob, getBlob } from 'src/app/common/network/crud/resourceCrud';
import { fileUpload } from 'src/app/common/utils/file-utils';
import { useDispatch } from 'react-redux';

const UploadedFile = () => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const { formFiles, setFormFiles } = useBatchFilesSubmitPageContext();
  const [isUploading, setIsUploading] = useState(false);
  const [blobDetail, setBlobDetail] = useState<{ url: string; blobId: string } | null>(null);
  const [isOpeningUrl, setIsOpeningUrl] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const ALLOWED_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-outlook',
    'message/rfc822',
  ];

  const ALLOWED_MIME_TYPE_BY_EXTENSION: Record<string, string> = {
    msg: 'application/vnd.ms-outlook',
    eml: 'message/rfc822',
  };

  const getAllowedMimeType = (file: File) => {
    if (ALLOWED_TYPES.includes(file.type)) return file.type;

    const extension = file.name.split('.').pop()?.toLowerCase();
    return extension ? ALLOWED_MIME_TYPE_BY_EXTENSION[extension] : undefined;
  };

  const handleFile = async (file: File) => {
    const mimeType = getAllowedMimeType(file);

    if (file && mimeType) {
      setIsUploading(true);
      setBlobDetail(null);
      setFormFiles(null);
      setUploadedFile(null);
      try {
        const createdBlobRes = await createBlob({ mimeType, accessLevel: 'private', module: 'vn-pd-opm' }, dispatch);
        await fileUpload(createdBlobRes.url, file);
        const detail = (await getBlob({ resourceIds: createdBlobRes.blobId }, dispatch))?.[0];
        setFormFiles(detail);
        setUploadedFile(file);
        setBlobDetail({ url: detail.url, blobId: detail.blobId });
      } catch (_) {
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleRemove = () => {
    setFormFiles(null);
    setUploadedFile(null);
    setBlobDetail(null);
  };

  const openBlobUrl = async () => {
    if (!blobDetail || isOpeningUrl) return;
    setIsOpeningUrl(true);
    try {
      const blob = (await getBlob({ resourceIds: blobDetail.blobId }, dispatch))?.[0];
      window.open(blob.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      // silently fail
    } finally {
      setIsOpeningUrl(false);
    }
  };

  return (
    <>
      <Box
        className={classes.uploadFile}
        onClick={handleClick}
        sx={{
          pointerEvents: isUploading ? 'none' : undefined,
          opacity: isUploading ? 0.6 : 1,
        }}
      >
        {isUploading ? <CircularProgress size={48} /> : <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500' }} />}
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {isUploading ? Translation('opm_pd.uploading') : Translation('opm_pd.dragOrClick')}
        </Typography>
        {!isUploading && (
          <>
            <Typography variant="caption" color="text.disabled">
              {Translation('opm_pd.onlyPngJpegPdfDocxXlsxAreAllowed')}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {Translation('opm_pd.maximum5MEachFile')}
            </Typography>
          </>
        )}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg,.docx,.xlsx,.msg,.eml,application/vnd.ms-outlook,message/rfc822"
        hidden
        onChange={handleInputChange}
      />
      {formFiles && !isUploading && (
        <div className={classes.uploadFileItem}>
          <ArticleIcon color="primary" />
          {blobDetail ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
              <Typography
                variant="body2"
                className={classes.uploadFileItemName}
                sx={{
                  color: isOpeningUrl ? 'text.disabled' : 'primary.main',
                  textDecoration: 'underline',
                  cursor: isOpeningUrl ? 'default' : 'pointer',
                }}
                onClick={openBlobUrl}
              >
                {uploadedFile?.name}
              </Typography>
              {isOpeningUrl && <CircularProgress size={14} />}
            </Box>
          ) : (
            <Typography variant="body2" className={classes.uploadFileItemName}>
              {uploadedFile?.name}
            </Typography>
          )}
          <Box flex={1} />
          <IconButton size="small" onClick={handleRemove} aria-label={Translation('opm_pd.removeFile')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      )}
    </>
  );
};

const AdditionalInformation = () => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const { formNote, setFormNote } = useBatchFilesSubmitPageContext();

  return (
    <Paper>
      <AppPaperTitle>{Translation('opm_pd.additionalInformation')}</AppPaperTitle>
      <AppPaperContent>
        <UploadedFile />
        <TextField
          multiline
          minRows={5}
          maxRows={8}
          fullWidth
          value={formNote}
          onChange={(e) => setFormNote(e.target.value)}
          label={Translation('opm_pd.note')}
          variant="outlined"
          sx={{ mt: 2 }}
        />
      </AppPaperContent>
    </Paper>
  );
};

export default AdditionalInformation;
